import React from 'react';
import type { GetServerSideProps } from 'next';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  Link,
  VStack,
  Text,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { Field, Form } from 'react-final-form';
import * as yup from 'yup';
import NextHead from 'next/head';
import type { NextPageWithLayout } from '../../common/types';
import LayoutNoLogin from '../../components/LayoutNoLogin';
import getConfig, { getPublicRuntimeConfig } from '../../common/config';
import useApiClient from '../../hooks/useApiClient';
import withAuth from '../../server/libs/withAuth';
import { title } from '../../common/libs/metadata';

type FormValues = {
  email?: string;
};

type ValidationErrors = {
  email: string | null;
};

type SubmitErrors = {
  email: string | null;
  accountType: JSX.Element | null;
  network: string | null;
};

function QaResetPasswordLink() {
  const {
    publicRuntimeConfig: { qaResetPasswordUrl },
  } = getConfig();
  return (
    <Text>
      Antaaアカウントのパスワード変更は
      <Link href={qaResetPasswordUrl} target="_blank" rel="noopener">
        こちら
      </Link>
      から
    </Text>
  );
}
function QaPasswordNotice() {
  return (
    <Alert status="warning" mt={4}>
      {QaResetPasswordLink()}
    </Alert>
  );
}

const PasswordReissuePage: NextPageWithLayout = () => {
  const { apiClient } = useApiClient();
  const onSubmit = async (
    values: FormValues,
  ): Promise<Partial<SubmitErrors> | undefined> => {
    try {
      const res = await apiClient('/api/account/resetPasswordRequest', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(values),
      });

      if (res.ok) {
        // NOP
      } else if (res.status === 403) {
        const data = await res.json();
        return {
          accountType: (
            <>
              {data.error.message}
              {QaResetPasswordLink()}
            </>
          ),
        };
      } else if (res.status === 404) {
        const data = await res.json();
        return {
          email: data.error.message,
        };
      } else {
        throw new Error();
      }
    } catch (e) {
      return {
        network: '時間をおいて再度お試しください。',
      };
    }
  };

  const validate = (values: FormValues): ValidationErrors | undefined => {
    const errors: ValidationErrors = {
      email: null,
    };

    const schemas = {
      email: yup.string().email().required(),
    };

    if (!schemas.email.isValidSync(values.email)) {
      errors.email = 'メールアドレスを入力してください。';
    }

    const hasNoError = Object.values(errors).every((v) => v === null);
    if (hasNoError) {
      return undefined;
    } else {
      return errors;
    }
  };

  const { slideWebBaseUrl } = getPublicRuntimeConfig();
  const canonicalUrl = `${slideWebBaseUrl}/account/reissue`;

  return (
    <>
      <NextHead>
        <title>{title('パスワード再発行')}</title>
        <link rel="canonical" href={canonicalUrl} />
      </NextHead>
      <Heading as="h1">パスワード再発行</Heading>
      <QaPasswordNotice />
      <Box mt={8}>
        <Form
          onSubmit={onSubmit}
          validate={validate}
          render={({
            handleSubmit,
            submitting,
            submitSucceeded,
            hasSubmitErrors,
            submitErrors,
            hasValidationErrors,
          }) => (
            <VStack as="form" onSubmit={handleSubmit} spacing={4}>
              <Field name="email">
                {({ input, meta }) => (
                  <FormControl
                    id="email"
                    isInvalid={meta.touched && meta.error}
                  >
                    <FormLabel>メールアドレス</FormLabel>
                    <Input
                      isRequired
                      type="text"
                      {...input}
                      placeholder="メールアドレス"
                    />
                    <FormErrorMessage>{meta.error}</FormErrorMessage>
                  </FormControl>
                )}
              </Field>

              {hasSubmitErrors &&
                Object.entries(submitErrors ?? {}).map(([key, val]) => (
                  <Alert status="error" key={key}>
                    <AlertIcon />
                    <Box>
                      <AlertTitle>
                        パスワード再設定メールの送信に失敗しました。
                      </AlertTitle>
                      <AlertDescription>{val}</AlertDescription>
                    </Box>
                  </Alert>
                ))}
              {submitSucceeded && (
                <Alert status="info">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>
                      パスワード再設定メールを送信しました。
                    </AlertTitle>
                    <AlertDescription>
                      メールに書かれたリンクをクリックして、1時間以内にパスワード再発行を行ってください。
                      <br />
                      <br />
                      <Text fontSize="sm">
                        メールが送られてこない場合は宛先メールアドレスの誤り、または迷惑メールフィルタ等の受信設定の影響が考えられます。
                        宛先メールアドレスおよび受信設定をご確認の上再度お試しください。
                      </Text>
                    </AlertDescription>
                  </Box>
                </Alert>
              )}

              <Button
                type="submit"
                colorScheme="brand"
                isDisabled={hasValidationErrors}
                isLoading={submitting}
              >
                再発行
              </Button>
            </VStack>
          )}
        />
      </Box>
    </>
  );
};

PasswordReissuePage.getLayout = (page) => <LayoutNoLogin>{page}</LayoutNoLogin>;

export default PasswordReissuePage;

export const getServerSideProps: GetServerSideProps<{}> = withAuth(async () => {
  return {
    props: {},
  };
});
